(() => {
  'use strict';

  const SELECTORS = {
    root: '[data-product-customizer]',
    toggle: '[data-customizer-toggle]',
    panel: '[data-customizer-panel]',
    personalizationText: '[data-personalization-text]',
    personalizationCounter: '[data-personalization-counter]',
    giftMessage: '[data-gift-message]',
    giftMessageCounter: '[data-gift-message-counter]',
    giftStandardPanel: '[data-gift-standard-panel]',
    giftHandwrittenNote: '[data-gift-handwritten-note]',
    giftCardChoice: '[data-gift-card-choice]',
    giftStyleChoice: '[data-gift-style]',
    giftCardProperty: '[data-gift-card-property]',
    giftStyleProperty: '[data-gift-style-property]',
    videoButton: '[data-customizer-video], [data-customizer-video-template]',
    videoModal: '[data-customizer-video-modal]',
    videoFrame: '[data-customizer-video-frame]',
    videoClose: '[data-customizer-video-close]',
    upsells: '[data-product-upsells]',
    upsellItem: '[data-upsell-item]',
    upsellToggle: '[data-upsell-toggle]',
    upsellVariantSelect: '[data-upsell-variant-select]',
    overviewDetails: '[data-smooth-overview]',
    overviewContent: '[data-overview-content]',
    overviewScrollLink: '[data-overview-scroll-link]',
    variantMap: '[data-customizer-variant-map]',
  };

  class ProductCustomizer {
    constructor(root) {
      this.root = root;
      this.section = root.closest('product-info') || root.closest('.shopify-section') || document;
      this.mainForm = document.getElementById(root.dataset.mainFormId) || null;
      this.upsells = this.section.querySelector(SELECTORS.upsells);
      this.panelTimers = new WeakMap();
      this.lastVideoTrigger = null;
      this.videoModal = root.querySelector(SELECTORS.videoModal) || null;
      this.variantMap = null;

      this.handleToggleChange = this.handleToggleChange.bind(this);
      this.handleChoiceClick = this.handleChoiceClick.bind(this);
      this.handleUpsellClick = this.handleUpsellClick.bind(this);
      this.handleVideoOpen = this.handleVideoOpen.bind(this);
      this.handleVideoClose = this.handleVideoClose.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleTextInput = this.handleTextInput.bind(this);

      this.init();
    }

    init() {
      if (this.root.dataset.customizerInitialized === 'true') return;

      this.root.dataset.customizerInitialized = 'true';
      this.root.productCustomizer = this;

      this.root.querySelectorAll(SELECTORS.toggle).forEach((toggle) => {
        toggle.addEventListener('change', this.handleToggleChange);
        this.syncToggle(toggle, false);
      });

      this.root
        .querySelectorAll(`${SELECTORS.giftCardChoice}, ${SELECTORS.giftStyleChoice}`)
        .forEach((choice) => choice.addEventListener('click', this.handleChoiceClick));

      const textInput = this.root.querySelector(SELECTORS.personalizationText);
      if (textInput) {
        textInput.addEventListener('input', this.handleTextInput);
        this.updateCharacterCounter(textInput, SELECTORS.personalizationCounter);
      }

      const giftMessage = this.root.querySelector(SELECTORS.giftMessage);
      if (giftMessage) {
        giftMessage.addEventListener('input', this.handleTextInput);
        this.updateCharacterCounter(giftMessage, SELECTORS.giftMessageCounter);
      }

      this.root.querySelectorAll(SELECTORS.videoButton).forEach((button) => {
        button.addEventListener('click', this.handleVideoOpen);
      });

      (this.videoModal || this.root)
        .querySelectorAll(SELECTORS.videoClose)
        .forEach((button) => {
          button.addEventListener('click', this.handleVideoClose);
        });

      this.upsells?.querySelectorAll(SELECTORS.upsellToggle).forEach((button) => {
        if (button.dataset.upsellBound === 'true') return;
        button.dataset.upsellBound = 'true';
        button.addEventListener('click', this.handleUpsellClick);
      });

      this.syncAllChoices();
      this.syncAllUpsells();
      document.addEventListener('keydown', this.handleKeyDown);

      this.dispatchChange();
    }

    destroy() {
      this.root.querySelectorAll(SELECTORS.toggle).forEach((toggle) => {
        toggle.removeEventListener('change', this.handleToggleChange);
      });

      this.root
        .querySelectorAll(`${SELECTORS.giftCardChoice}, ${SELECTORS.giftStyleChoice}`)
        .forEach((choice) => choice.removeEventListener('click', this.handleChoiceClick));

      const textInput = this.root.querySelector(SELECTORS.personalizationText);
      textInput?.removeEventListener('input', this.handleTextInput);

      const giftMessage = this.root.querySelector(SELECTORS.giftMessage);
      giftMessage?.removeEventListener('input', this.handleTextInput);

      this.root.querySelectorAll(SELECTORS.videoButton).forEach((button) => {
        button.removeEventListener('click', this.handleVideoOpen);
      });

      (this.videoModal || this.root)
        .querySelectorAll(SELECTORS.videoClose)
        .forEach((button) => {
          button.removeEventListener('click', this.handleVideoClose);
        });

      this.upsells?.querySelectorAll(SELECTORS.upsellToggle).forEach((button) => {
        button.removeEventListener('click', this.handleUpsellClick);
        delete button.dataset.upsellBound;
      });

      document.removeEventListener('keydown', this.handleKeyDown);

      if (this.videoModal && this.videoModal.parentElement === document.body) {
        this.videoModal.querySelector('video')?.pause?.();
        this.videoModal.remove();
        this.videoModal = null;
      }

      delete this.root.productCustomizer;
      delete this.root.dataset.customizerInitialized;
    }

    handleToggleChange(event) {
      this.syncToggle(event.currentTarget, true);
      this.dispatchChange();
    }

    syncToggle(toggle, animate = true) {
      const type = toggle.dataset.customizerToggle;
      const panel = this.root.querySelector(
        `${SELECTORS.panel}[data-customizer-panel="${type}"]`
      );
      const enabled = toggle.checked && !toggle.disabled;
      const group = toggle.closest('[data-customizer-group]');

      group?.classList.toggle('is-enabled', enabled);
      toggle.setAttribute('aria-expanded', enabled ? 'true' : 'false');

      if (panel) {
        enabled
          ? this.openPanel(panel, animate)
          : this.closePanel(panel, animate);
      }

      if (type === 'personalization') {
        const input = this.root.querySelector(SELECTORS.personalizationText);

        if (input) {
          input.disabled = !enabled;
          input.required = enabled;

          if (!enabled) {
            input.setCustomValidity('');
          }
        }
      }

      if (type === 'gift') {
        this.root
          .querySelectorAll(
            `${SELECTORS.giftCardProperty}, ${SELECTORS.giftStyleProperty}`
          )
          .forEach((input) => {
            input.disabled = !enabled;
          });

        const currentStyle = this.getSelectedGiftStyle().key;
        this.syncGiftStylePanels(currentStyle, enabled);

        if (!enabled) {
          const giftMessage = this.root.querySelector(SELECTORS.giftMessage);
          giftMessage?.setCustomValidity('');
        }
      }
    }

    openPanel(panel, animate = true) {
      const oldTimer = this.panelTimers.get(panel);
      if (oldTimer) window.clearTimeout(oldTimer);

      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      panel.classList.add('is-open');

      if (!animate) {
        panel.style.maxHeight = 'none';
        panel.style.opacity = '1';
        panel.style.transform = 'none';
        panel.style.overflow = 'visible';
        return;
      }

      panel.style.overflow = 'hidden';
      panel.style.maxHeight = '0px';
      panel.style.opacity = '0';
      panel.style.transform = 'translateY(-6px)';
      panel.style.transition =
        'max-height 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease, transform 220ms ease';

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          panel.style.maxHeight = `${panel.scrollHeight}px`;
          panel.style.opacity = '1';
          panel.style.transform = 'translateY(0)';
        });
      });

      const timer = window.setTimeout(() => {
        panel.style.maxHeight = 'none';
        panel.style.overflow = 'visible';
        this.panelTimers.delete(panel);
      }, 340);

      this.panelTimers.set(panel, timer);
    }

    closePanel(panel, animate = true) {
      const oldTimer = this.panelTimers.get(panel);
      if (oldTimer) window.clearTimeout(oldTimer);

      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');

      if (!animate || panel.hidden) {
        panel.hidden = true;
        panel.style.maxHeight = '0px';
        panel.style.opacity = '0';
        panel.style.transform = 'translateY(-6px)';
        panel.style.overflow = 'hidden';
        return;
      }

      panel.style.transition =
        'max-height 280ms cubic-bezier(0.4, 0, 1, 1), opacity 180ms ease, transform 180ms ease';
      panel.style.overflow = 'hidden';
      panel.style.maxHeight = `${panel.scrollHeight}px`;
      panel.style.opacity = '1';
      panel.style.transform = 'translateY(0)';

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          panel.style.maxHeight = '0px';
          panel.style.opacity = '0';
          panel.style.transform = 'translateY(-6px)';
        });
      });

      const timer = window.setTimeout(() => {
        panel.hidden = true;
        this.panelTimers.delete(panel);
      }, 300);

      this.panelTimers.set(panel, timer);
    }

    handleTextInput(event) {
      const input = event.currentTarget;
      input.setCustomValidity('');

      /*
       * Personalization lettering is always uppercase visually and in the
       * submitted Shopify line-item property.
       */
      if (input.matches(SELECTORS.personalizationText)) {
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;
        const upperValue = String(input.value || '').toLocaleUpperCase();

        if (input.value !== upperValue) {
          input.value = upperValue;

          if (
            typeof selectionStart === 'number' &&
            typeof selectionEnd === 'number'
          ) {
            input.setSelectionRange(selectionStart, selectionEnd);
          }
        }
      }

      const counterSelector = input.matches(SELECTORS.giftMessage)
        ? SELECTORS.giftMessageCounter
        : SELECTORS.personalizationCounter;

      this.updateCharacterCounter(input, counterSelector);
      this.dispatchChange();
    }

    updateCharacterCounter(input, counterSelector) {
      const counter = this.root.querySelector(counterSelector);
      if (!counter) return;

      const max = Number.parseInt(input.maxLength, 10);
      const used = Array.from(input.value || '').length;
      const remaining = Number.isFinite(max) && max > 0
        ? Math.max(0, max - used)
        : 0;

      counter.textContent = Number.isFinite(max) && max > 0
        ? `${remaining}/${max} characters left`
        : `${used} characters`;

      counter.classList.toggle(
        'is-limit-reached',
        Number.isFinite(max) && max > 0 && remaining === 0
      );
    }

    handleChoiceClick(event) {
      const button = event.currentTarget;
      if (button.disabled) return;

      if (button.matches(SELECTORS.giftCardChoice)) {
        this.selectChoice(
          button,
          SELECTORS.giftCardChoice,
          SELECTORS.giftCardProperty,
          button.dataset.giftCardChoice
        );
      } else if (button.matches(SELECTORS.giftStyleChoice)) {
        const property = this.root.querySelector(SELECTORS.giftStyleProperty);
        const style = button.dataset.giftStyle;
        const value = style === 'handwritten'
          ? property?.dataset.handwrittenLabel || button.textContent.trim()
          : property?.dataset.standardLabel || button.textContent.trim();

        this.selectChoice(
          button,
          SELECTORS.giftStyleChoice,
          SELECTORS.giftStyleProperty,
          value
        );
        this.syncGiftStylePanels(
          style,
          this.isEnabled('gift')
        );
      }

      this.dispatchChange();
    }

    selectChoice(button, selector, propertySelector, propertyValue) {
      const container = button.closest('.product-customizer__choices');
      const choices = container
        ? container.querySelectorAll(selector)
        : this.root.querySelectorAll(selector);

      choices.forEach((choice) => {
        const selected = choice === button;
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      const property = this.root.querySelector(propertySelector);
      if (property) property.value = propertyValue || '';
    }

    syncAllChoices() {
      const selectedCard =
        this.root.querySelector(`${SELECTORS.giftCardChoice}.is-selected`) ||
        this.root.querySelector(SELECTORS.giftCardChoice);

      if (selectedCard) {
        this.selectChoice(
          selectedCard,
          SELECTORS.giftCardChoice,
          SELECTORS.giftCardProperty,
          selectedCard.dataset.giftCardChoice
        );
      }

      const selectedStyle =
        this.root.querySelector(`${SELECTORS.giftStyleChoice}.is-selected`) ||
        this.root.querySelector(SELECTORS.giftStyleChoice);

      if (selectedStyle) {
        const property = this.root.querySelector(SELECTORS.giftStyleProperty);
        const style = selectedStyle.dataset.giftStyle;
        const value = style === 'handwritten'
          ? property?.dataset.handwrittenLabel || selectedStyle.textContent.trim()
          : property?.dataset.standardLabel || selectedStyle.textContent.trim();

        this.selectChoice(
          selectedStyle,
          SELECTORS.giftStyleChoice,
          SELECTORS.giftStyleProperty,
          value
        );
        this.syncGiftStylePanels(
          style,
          this.isEnabled('gift')
        );
      }
    }

    syncGiftStylePanels(style = 'standard', giftEnabled = this.isEnabled('gift')) {
      const isHandwritten = style === 'handwritten';
      const standardPanel = this.root.querySelector(SELECTORS.giftStandardPanel);
      const handwrittenNote = this.root.querySelector(SELECTORS.giftHandwrittenNote);
      const giftMessage = this.root.querySelector(SELECTORS.giftMessage);

      if (standardPanel) {
        standardPanel.hidden = isHandwritten;
        standardPanel.setAttribute('aria-hidden', isHandwritten ? 'true' : 'false');
      }

      if (handwrittenNote) {
        handwrittenNote.hidden = !isHandwritten;
        handwrittenNote.setAttribute('aria-hidden', isHandwritten ? 'false' : 'true');
      }

      if (giftMessage) {
        const shouldEnableMessage = giftEnabled && !isHandwritten;
        giftMessage.disabled = !shouldEnableMessage;
        giftMessage.required =
          shouldEnableMessage &&
          giftMessage.dataset.required === 'true';

        if (!shouldEnableMessage) {
          giftMessage.setCustomValidity('');
        }
      }
    }

    handleUpsellClick(event) {
      addUpsellToCart(event.currentTarget);
    }

    syncAllUpsells() {
      this.upsells?.querySelectorAll(SELECTORS.upsellToggle).forEach((button) => {
        resetUpsellButton(button);
      });
    }

    handleVideoOpen(event) {
      event.preventDefault();

      const button = event.currentTarget;
      const url = button.dataset.customizerVideo;
      const templateId = button.dataset.customizerVideoTemplate;

      const modal =
        this.videoModal ||
        this.root.querySelector(SELECTORS.videoModal) ||
        document.querySelector(SELECTORS.videoModal);

      const frame = modal?.querySelector(SELECTORS.videoFrame);
      if (!modal || !frame) return;

      this.videoModal = modal;

      /*
       * A fixed element becomes relative to a transformed ancestor. Product
       * cards/sticky wrappers can therefore push a modal into a corner.
       * Portalling it to <body> guarantees true viewport centering.
       */
      if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }

      let content = null;

      if (templateId) {
        const template = document.getElementById(templateId);

        if (template instanceof HTMLTemplateElement) {
          content = template.content.cloneNode(true);
        }
      }

      if (!content && url) {
        content = this.createVideoElement(url);
      }

      if (!content) return;

      /*
       * Remove anything left from a previous tutorial first so old players
       * cannot continue audio/video behind the newly opened popup.
       */
      frame.replaceChildren();
      frame.append(content);

      const hostedVideo = frame.querySelector('video');

      if (hostedVideo) {
        hostedVideo.controls = true;
        hostedVideo.autoplay = true;
        hostedVideo.muted = true;
        hostedVideo.defaultMuted = true;
        hostedVideo.playsInline = true;
        hostedVideo.preload = 'auto';

        /*
         * Call play() while still inside the user's click handler. This is
         * more reliable than waiting until an animation frame.
         */
        const playPromise = hostedVideo.play();
        playPromise?.catch?.(() => {
          /*
           * Native controls remain visible if a browser still refuses
           * autoplay for an unusual device/browser policy.
           */
        });
      }

      this.lastVideoTrigger = button;

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          modal.classList.add('is-open');
          modal.querySelector('.product-customizer__video-close')?.focus();
        });
      });
    }

    handleVideoClose() {
      const modal =
        this.videoModal ||
        document.querySelector(SELECTORS.videoModal);

      const frame = modal?.querySelector(SELECTORS.videoFrame);
      if (!modal) return;

      const hostedVideo = frame?.querySelector('video');
      hostedVideo?.pause?.();

      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');

      window.setTimeout(() => {
        /*
         * Removing the iframe/video is intentional: it guarantees YouTube,
         * Vimeo and Shopify-hosted playback all stop completely on close.
         */
        frame?.replaceChildren();
        modal.hidden = true;

        this.lastVideoTrigger?.focus();
        this.lastVideoTrigger = null;
      }, 240);
    }

    handleKeyDown(event) {
      if (event.key !== 'Escape') return;

      const modal =
        this.videoModal ||
        document.querySelector(SELECTORS.videoModal);

      if (modal && !modal.hidden) {
        event.preventDefault();
        this.handleVideoClose();
      }
    }

    createVideoElement(rawUrl) {
      let url;

      try {
        url = new URL(String(rawUrl || '').trim(), window.location.origin);
      } catch (error) {
        const message = document.createElement('p');
        message.className = 'product-customizer__video-error';
        message.textContent = 'The tutorial video URL is invalid.';
        return message;
      }

      const host = url.hostname.replace(/^www\./, '').toLowerCase();
      const pathParts = url.pathname.split('/').filter(Boolean);
      let embedUrl = null;

      /* YouTube: watch, youtu.be, shorts and embed URLs. */
      if (host === 'youtu.be' && pathParts[0]) {
        const videoId = pathParts[0];
        embedUrl =
          `https://www.youtube.com/embed/${encodeURIComponent(videoId)}` +
          '?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1';
      } else if (
        host.endsWith('youtube.com') ||
        host.endsWith('youtube-nocookie.com')
      ) {
        let videoId = url.searchParams.get('v');

        if (
          !videoId &&
          ['embed', 'shorts', 'live'].includes(pathParts[0]) &&
          pathParts[1]
        ) {
          videoId = pathParts[1];
        }

        if (videoId) {
          embedUrl =
            `https://www.youtube.com/embed/${encodeURIComponent(videoId)}` +
            '?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1';
        }
      }

      /* Vimeo, including unlisted Vimeo links that contain a hash. */
      if (!embedUrl && host.endsWith('vimeo.com')) {
        const numericIndex = pathParts.findIndex((part) => /^\\d+$/.test(part));
        const videoId = numericIndex >= 0 ? pathParts[numericIndex] : null;
        const privateHash =
          numericIndex >= 0 && pathParts[numericIndex + 1]
            ? pathParts[numericIndex + 1]
            : '';

        if (videoId) {
          const params = new URLSearchParams({
            autoplay: '1',
            muted: '1',
            playsinline: '1',
          });

          if (privateHash) params.set('h', privateHash);

          embedUrl =
            `https://player.vimeo.com/video/${encodeURIComponent(videoId)}` +
            `?${params.toString()}`;
        }
      }

      const extension = url.pathname
        .split('.')
        .pop()
        ?.toLowerCase();

      const isDirectVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(extension);

      if (isDirectVideo) {
        const video = document.createElement('video');
        video.src = url.href;
        video.controls = true;
        video.autoplay = true;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.preload = 'auto';
        return video;
      }

      if (!embedUrl) {
        const message = document.createElement('p');
        message.className = 'product-customizer__video-error';
        message.textContent = 'Please use a YouTube, Vimeo, or Shopify-hosted tutorial video.';
        return message;
      }

      const iframe = document.createElement('iframe');
      iframe.src = embedUrl;
      iframe.title = 'Tutorial video';
      iframe.allow =
        'autoplay; fullscreen; encrypted-media; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.loading = 'eager';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      return iframe;
    }

    validate() {
      const personalizationEnabled = this.isEnabled('personalization');
      const input = this.root.querySelector(SELECTORS.personalizationText);

      if (personalizationEnabled && input && !input.value.trim()) {
        input.setCustomValidity('Please enter your personalization text.');
        input.reportValidity();
        input.focus();
        return false;
      }

      input?.setCustomValidity('');

      const giftEnabled = this.isEnabled('gift');
      const giftMessage = this.root.querySelector(SELECTORS.giftMessage);
      const giftMessageRequired = giftMessage?.dataset.required === 'true';

      const giftStyle = this.getSelectedGiftStyle().key;

      if (
        giftEnabled &&
        giftStyle === 'standard' &&
        giftMessage &&
        giftMessageRequired &&
        !giftMessage.value.trim()
      ) {
        giftMessage.setCustomValidity('Please enter your greeting message.');
        giftMessage.reportValidity();
        giftMessage.focus();
        return false;
      }

      giftMessage?.setCustomValidity('');
      return true;
    }

    isEnabled(type) {
      const toggle = this.root.querySelector(
        `${SELECTORS.toggle}[data-customizer-toggle="${type}"]`
      );

      return Boolean(toggle?.checked && !toggle.disabled);
    }

    getSelectedGiftCard() {
      return (
        this.root.querySelector(`${SELECTORS.giftCardChoice}.is-selected`)
          ?.dataset.giftCardChoice || ''
      );
    }

    getSelectedGiftStyle() {
      const button = this.root.querySelector(
        `${SELECTORS.giftStyleChoice}.is-selected`
      );
      const property = this.root.querySelector(SELECTORS.giftStyleProperty);

      return {
        key: button?.dataset.giftStyle || 'standard',
        label: property?.value || '',
      };
    }

    getVariantMap() {
      if (Array.isArray(this.variantMap)) return this.variantMap;

      const script = this.root.querySelector(SELECTORS.variantMap);
      if (!script) {
        this.variantMap = [];
        return this.variantMap;
      }

      try {
        const parsed = JSON.parse(script.textContent || '[]');
        this.variantMap = Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.warn('Unable to read product variants for customizer line-item labels.', error);
        this.variantMap = [];
      }

      return this.variantMap;
    }

    getParentProductContext() {
      const productTitle = String(
        this.root.dataset.parentProductTitle || ''
      ).trim();

      const variantInput =
        this.mainForm?.querySelector('input[name="id"]') ||
        this.section?.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');

      const variantId = this.parseVariantId(variantInput?.value);
      const variant = this.getVariantMap().find(
        (candidate) => Number(candidate?.id) === Number(variantId)
      );

      let variantLabel = '';

      if (variant) {
        const options = Array.isArray(variant.options)
          ? variant.options
              .map((option) => String(option || '').trim())
              .filter(
                (option) =>
                  option &&
                  option.toLowerCase() !== 'default title'
              )
          : [];

        if (options.length) {
          variantLabel = options.join(' / ');
        } else {
          const title = String(variant.title || '').trim();
          if (title && title.toLowerCase() !== 'default title') {
            variantLabel = title;
          }
        }
      }

      return {
        title: productTitle || 'Product',
        variantId,
        variantLabel,
      };
    }

    getHandwrittenNoteText() {
      const note = this.root.querySelector(SELECTORS.giftHandwrittenNote);
      const text = String(note?.textContent || '')
        .replace(/\s+/g, ' ')
        .trim();

      return text || 'To be shared on WhatsApp.';
    }

    buildParentLineValue(parent, parts = []) {
      const productTitle = String(parent?.title || '').trim();
      const variantLabel = String(parent?.variantLabel || '').trim();

      const parentLabel = [productTitle, variantLabel]
        .filter(Boolean)
        .join(': ');

      return [parentLabel, ...parts]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' | ');
    }

    getState() {
      const personalizationText =
        this.root.querySelector(SELECTORS.personalizationText)?.value.trim() || '';
      const giftStyle = this.getSelectedGiftStyle();
      const giftMessageInput = this.root.querySelector(SELECTORS.giftMessage);
      const giftMessage =
        giftStyle.key === 'standard'
          ? giftMessageInput?.value.trim() || ''
          : '';

      return {
        parent: this.getParentProductContext(),
        personalization: {
          enabled: this.isEnabled('personalization'),
          variantId: this.parseVariantId(this.root.dataset.personalizationVariantId),
          text: personalizationText,
          propertyLabel:
            this.root.querySelector(SELECTORS.personalizationText)?.dataset.propertyLabel ||
            'Personalization',
        },
        gift: {
          enabled: this.isEnabled('gift'),
          variantId: this.parseVariantId(this.root.dataset.giftVariantId),
          card: this.getSelectedGiftCard(),
          style: giftStyle.key,
          styleLabel: giftStyle.label,
          message: giftMessage,
          messagePropertyLabel:
            giftMessageInput?.dataset.propertyLabel || 'Greeting Message',
          handwrittenVariantId: this.parseVariantId(
            this.root.dataset.handwrittenVariantId
          ),
          handwrittenNoteText: this.getHandwrittenNoteText(),
        },
        upsells: this.getSelectedUpsells(),
      };
    }

    getSelectedUpsells() {
      if (!this.upsells) return [];

      return Array.from(
        this.upsells.querySelectorAll(`${SELECTORS.upsellItem}.is-selected`)
      )
        .map((item) => ({
          variantId: this.parseVariantId(item.dataset.variantId),
          productId: this.parseVariantId(item.dataset.productId),
          title: item.dataset.productTitle || '',
        }))
        .filter((item) => item.variantId);
    }

    getAddOnItems({ bundleId = '', quantity = 1 } = {}) {
      const state = this.getState();
      const normalizedQuantity = Math.max(1, Number.parseInt(quantity, 10) || 1);
      const items = [];
      const parentKey = state.parent.title;

      const baseProperties = (role) => ({
        ...(bundleId ? { '_Bundle ID': bundleId } : {}),
        '_Bundle role': role,
      });

      if (state.personalization.enabled && state.personalization.variantId) {
        items.push({
          id: state.personalization.variantId,
          quantity: normalizedQuantity,
          properties: {
            ...baseProperties('Personalization'),
            [parentKey]: this.buildParentLineValue(state.parent, [
              `Personalization: ${state.personalization.text}`,
            ]),
          },
        });
      }

      if (state.gift.enabled && state.gift.variantId) {
        const giftParts = [`Card: ${state.gift.card}`];

        if (state.gift.style === 'standard' && state.gift.message) {
          giftParts.push(`Standard Note: ${state.gift.message}`);
        }

        items.push({
          id: state.gift.variantId,
          quantity: normalizedQuantity,
          properties: {
            ...baseProperties('Gift'),
            [parentKey]: this.buildParentLineValue(state.parent, giftParts),
          },
        });
      }

      if (
        state.gift.enabled &&
        state.gift.style === 'handwritten' &&
        state.gift.handwrittenVariantId
      ) {
        items.push({
          id: state.gift.handwrittenVariantId,
          quantity: normalizedQuantity,
          properties: {
            ...baseProperties('Hand-written note'),
            [parentKey]: this.buildParentLineValue(state.parent, [
              `Card: ${state.gift.card}`,
              `Hand-Written Note: ${state.gift.handwrittenNoteText}`,
            ]),
          },
        });
      }

      return items;
    }

    getMainProductProperties() {
      return {};
    }

    parseVariantId(value) {
      const id = Number.parseInt(value, 10);
      return Number.isFinite(id) && id > 0 ? id : null;
    }

    dispatchChange() {
      this.root.dispatchEvent(
        new CustomEvent('product-customizer:change', {
          bubbles: true,
          detail: this.getState(),
        })
      );
    }
  }


  function syncUpsellVariantSelect(select) {
    if (!select) return;

    const item = select.closest(SELECTORS.upsellItem);
    if (!item) return;

    const option = select.selectedOptions?.[0];
    if (!option) return;

    const variantId = Number.parseInt(option.value, 10);
    if (Number.isFinite(variantId) && variantId > 0) {
      item.dataset.variantId = String(variantId);
    }

    const price = item.querySelector('[data-upsell-price]');
    const comparePrice = item.querySelector('[data-upsell-compare-price]');
    const button = item.querySelector(SELECTORS.upsellToggle);
    const image = item.querySelector('.custom-product-upsells__image img');
    const available =
      option.dataset.available !== 'false' &&
      !option.disabled;

    if (price && option.dataset.price) {
      price.textContent = option.dataset.price;
    }

    if (comparePrice) {
      const compareText = option.dataset.comparePrice || '';
      comparePrice.textContent = compareText;
      comparePrice.hidden = !compareText;
    }

    if (image && option.dataset.imageUrl) {
      image.src = option.dataset.imageUrl;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
    }

    if (button && !button.classList.contains('is-loading')) {
      button.disabled = !available;
      const productTitle = item.dataset.productTitle || 'product';
      button.setAttribute(
        'aria-label',
        `${available ? 'Add' : 'Unavailable'} ${productTitle}${available ? ' to cart' : ''}`
      );
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  async function toggleOverview(details) {
    if (!details || details.dataset.overviewAnimating === 'true') return;

    const summary = details.querySelector(':scope > summary');
    const content = details.querySelector(SELECTORS.overviewContent);
    if (!summary || !content) return;

    const opening = !details.open;
    details.dataset.overviewAnimating = 'true';

    try {
      if (opening) {
        details.open = true;
        details.classList.add('is-expanded');
        summary.setAttribute('aria-expanded', 'true');

        if (prefersReducedMotion() || typeof content.animate !== 'function') {
          content.style.height = 'auto';
          content.style.opacity = '1';
          content.style.transform = 'none';
          content.style.overflow = 'visible';
          return;
        }

        content.style.height = '0px';
        content.style.opacity = '0';
        content.style.transform = 'translateY(-5px)';
        content.style.overflow = 'hidden';

        const targetHeight = content.scrollHeight;
        const animation = content.animate(
          [
            { height: '0px', opacity: 0, transform: 'translateY(-5px)' },
            { height: `${targetHeight}px`, opacity: 1, transform: 'translateY(0)' },
          ],
          {
            duration: 320,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          }
        );

        await animation.finished.catch(() => {});
        content.style.height = 'auto';
        content.style.opacity = '1';
        content.style.transform = 'none';
        content.style.overflow = 'visible';
      } else {
        details.classList.remove('is-expanded');
        summary.setAttribute('aria-expanded', 'false');

        if (prefersReducedMotion() || typeof content.animate !== 'function') {
          details.open = false;
          content.style.height = '0px';
          content.style.opacity = '0';
          content.style.transform = 'translateY(-5px)';
          content.style.overflow = 'hidden';
          return;
        }

        const startHeight =
          content.getBoundingClientRect().height ||
          content.scrollHeight;

        content.style.height = `${startHeight}px`;
        content.style.opacity = '1';
        content.style.transform = 'translateY(0)';
        content.style.overflow = 'hidden';

        const animation = content.animate(
          [
            { height: `${startHeight}px`, opacity: 1, transform: 'translateY(0)' },
            { height: '0px', opacity: 0, transform: 'translateY(-5px)' },
          ],
          {
            duration: 280,
            easing: 'cubic-bezier(0.4, 0, 1, 1)',
          }
        );

        await animation.finished.catch(() => {});
        details.open = false;
        content.style.height = '0px';
        content.style.opacity = '0';
        content.style.transform = 'translateY(-5px)';
        content.style.overflow = 'hidden';
      }
    } finally {
      delete details.dataset.overviewAnimating;
    }
  }

  function initSmoothOverviews(container = document) {
    container.querySelectorAll(SELECTORS.overviewDetails).forEach((details) => {
      if (details.dataset.overviewBound === 'true') return;

      const summary = details.querySelector(':scope > summary');
      const content = details.querySelector(SELECTORS.overviewContent);
      if (!summary || !content) return;

      details.dataset.overviewBound = 'true';
      details.classList.toggle('is-expanded', details.open);
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

      if (details.open) {
        content.style.height = 'auto';
        content.style.opacity = '1';
        content.style.transform = 'none';
        content.style.overflow = 'visible';
      }

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        toggleOverview(details);
      });
    });

    container.querySelectorAll(SELECTORS.overviewScrollLink).forEach((link) => {
      if (link.dataset.overviewScrollBound === 'true') return;
      link.dataset.overviewScrollBound = 'true';

      link.addEventListener('click', (event) => {
        const rawTarget = (link.getAttribute('href') || '').trim();
        if (!rawTarget) return;

        const targetId = rawTarget.replace(/^#/, '');
        const target = document.getElementById(targetId);
        if (!target) return;

        event.preventDefault();

        const offset = Number.parseFloat(link.dataset.scrollOffset || '0') || 0;
        const top =
          target.getBoundingClientRect().top +
          window.scrollY -
          offset;

        window.scrollTo({
          top: Math.max(0, top),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      });
    });
  }

  function resetUpsellButton(button) {
    if (!button) return;

    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');

    const plus = button.querySelector('[data-upsell-plus]');
    const loader = button.querySelector('[data-upsell-loader]');

    if (plus) plus.hidden = false;
    if (loader) loader.hidden = true;
  }

  async function addUpsellToCart(button) {
    if (!button || button.disabled || button.classList.contains('is-loading')) {
      return;
    }

    const item = button.closest(SELECTORS.upsellItem);
    if (!item) return;

    const variantId = Number.parseInt(item.dataset.variantId, 10);
    if (!Number.isFinite(variantId) || variantId <= 0) return;

    const cart =
      document.querySelector('cart-drawer') ||
      document.querySelector('cart-notification');

    const plus = button.querySelector('[data-upsell-plus]');
    const loader = button.querySelector('[data-upsell-loader]');

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    if (plus) plus.hidden = true;
    if (loader) loader.hidden = false;

    const payload = {
      items: [
        {
          id: variantId,
          quantity: 1,
        },
      ],
    };

    if (cart?.getSectionsToRender) {
      payload.sections = cart
        .getSectionsToRender()
        .map((section) => section.id)
        .join(',');
      payload.sections_url = window.location.pathname;
      cart.setActiveElement?.(button);
    }

    try {
      const response = await fetch(
        window.routes?.cart_add_url || '/cart/add.js',
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(
          data.description ||
            data.message ||
            'Unable to add this product to cart.'
        );
      }

      if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-upsell',
          productVariantId: String(variantId),
          cartData: data,
        });
      }

      if (cart?.renderContents) {
        cart.renderContents(data);
      } else {
        window.location.assign(window.routes?.cart_url || '/cart');
      }
    } catch (error) {
      console.error('Upsell add-to-cart error:', error);

      const originalLabel = button.getAttribute('aria-label') || 'Add product to cart';
      button.setAttribute(
        'aria-label',
        error?.message || 'Unable to add product to cart'
      );

      window.setTimeout(() => {
        button.setAttribute('aria-label', originalLabel);
      }, 2500);
    } finally {
      button.disabled = false;
      resetUpsellButton(button);
    }
  }

  function initStandaloneUpsells(container = document) {
    container.querySelectorAll(SELECTORS.upsells).forEach((group) => {
      group.querySelectorAll(SELECTORS.upsellVariantSelect).forEach((select) => {
        syncUpsellVariantSelect(select);

        if (select.dataset.upsellVariantBound === 'true') return;
        select.dataset.upsellVariantBound = 'true';

        select.addEventListener('change', () => {
          syncUpsellVariantSelect(select);
        });
      });

      group.querySelectorAll(SELECTORS.upsellToggle).forEach((button) => {
        resetUpsellButton(button);

        if (button.dataset.upsellBound === 'true') return;
        button.dataset.upsellBound = 'true';
        button.addEventListener('click', () => {
          addUpsellToCart(button);
        });
      });
    });
  }

  function initProductCustomizers(container = document) {
    container.querySelectorAll(SELECTORS.root).forEach((root) => {
      if (root.dataset.customizerInitialized === 'true') return;
      new ProductCustomizer(root);
    });

    initStandaloneUpsells(container);
    initSmoothOverviews(container);
  }

  window.ProductCustomizer = ProductCustomizer;
  window.initProductCustomizers = initProductCustomizers;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initProductCustomizers());
  } else {
    initProductCustomizers();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initProductCustomizers(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTORS.root).forEach((root) => {
      root.productCustomizer?.destroy();
    });
  });
})();